import { addDays, format, getISOWeek, parseISO, startOfISOWeek } from 'date-fns';
import { nl } from 'date-fns/locale';

import { SERVICES, type Service } from '@/design-system';
import { api } from '@/shared/api';
import { unwrap } from '@/shared/api/envelope';

import {
    isOrderStatusCode,
    ORDER_STATUS_LABELS,
    serviceForCode,
    serviceLabelOf,
    toOrder,
    type InspectionType,
    type Order,
} from './model';
import {
    autoScheduleResultResourceSchema,
    orderResourceSchema,
    planningWeekResourceSchema,
    type AutoScheduleResultResource,
    type PlanningStopResource,
    type PlanningSuggestionResource,
    type PlanningWeekResource,
} from './types';

/**
 * Het weekrooster: één rij per inspecteur, een kolom per werkdag, per cel de
 * keuringen van die dag. De blokjes dragen de **dienstkleur**, niet de
 * statuskleur; die twee schalen lopen nooit door elkaar.
 */
export interface PlanningStop {
    orderUuid: string;
    orderNumber: string;
    statusLabel: string;
    scheduledAt: string;
    /** `09:00` - in mono in het blokje. */
    time: string;
    /** De plaats; het blokje is smal. */
    place: string;
    address: string;
    clientName: string | null;
    service: Service;
    serviceLabel: string;
    durationMinutes: number | null;
}

export interface PlanningCell {
    distanceKm: number;
    driveMinutes: number;
    available: boolean;
    stops: PlanningStop[];
}

export interface PlanningRow {
    inspectorUuid: string;
    name: string;
    stopCount: number;
    distanceKm: number;
    /** Per datum (`2026-09-21`). */
    cells: Record<string, PlanningCell>;
}

export interface PlanningDay {
    date: string;
    /** "ma 21 sep" */
    label: string;
    /** Kilometers van alle inspecteurs samen op die dag. */
    distanceKm: number;
}

/**
 * Een voorstel is altijd concreet: wát verplaatst er, waarheen, en hoeveel
 * kilometer scheelt dat (principe 7). De zin zelf komt van de API.
 */
export interface PlanningSuggestion {
    id: string;
    orderUuid: string;
    orderNumber: string;
    label: string;
    savedKilometres: number;
    proposedScheduledAt: string | null;
}

export interface PlanningWeek {
    from: string;
    to: string;
    weekNumber: number;
    totalDistanceKm: number;
    /** Keuringen die nog op een afspraak wachten. */
    unscheduledCount: number;
    days: PlanningDay[];
    rows: PlanningRow[];
    suggestions: PlanningSuggestion[];
}

function toStop(resource: PlanningStopResource): PlanningStop {
    const first = resource.services[0];

    return {
        orderUuid: resource.order_uuid,
        orderNumber: resource.order_number,
        statusLabel: isOrderStatusCode(resource.status)
            ? ORDER_STATUS_LABELS[resource.status]
            : resource.status,
        scheduledAt: resource.scheduled_at,
        time: format(parseISO(resource.scheduled_at), 'HH:mm'),
        place: resource.city ?? resource.address,
        address: resource.address,
        clientName: resource.client_name,
        service: serviceForCode(first?.code),
        serviceLabel: serviceLabelOf(resource.services),
        durationMinutes: resource.duration_minutes,
    };
}

function toSuggestion(resource: PlanningSuggestionResource): PlanningSuggestion {
    return {
        id: resource.id,
        orderUuid: resource.order_uuid,
        orderNumber: resource.order_number,
        label: resource.label,
        savedKilometres: resource.saved_kilometres,
        proposedScheduledAt: resource.proposed_scheduled_at,
    };
}

export function toPlanningWeek(resource: PlanningWeekResource): PlanningWeek {
    const rows = new Map<string, PlanningRow>();
    const perDay = new Map<string, number>();

    for (const day of resource.days) {
        const row = rows.get(day.inspector_uuid) ?? {
            inspectorUuid: day.inspector_uuid,
            name: day.inspector_name,
            stopCount: 0,
            distanceKm: 0,
            cells: {},
        };
        const stops = [...day.stops]
            .sort((a, b) => a.scheduled_at.localeCompare(b.scheduled_at))
            .map(toStop);

        row.cells[day.date] = {
            distanceKm: day.distance_km,
            driveMinutes: day.drive_minutes,
            available: day.available,
            stops,
        };
        row.stopCount += stops.length;
        row.distanceKm += day.distance_km;
        rows.set(day.inspector_uuid, row);
        perDay.set(day.date, (perDay.get(day.date) ?? 0) + day.distance_km);
    }

    const days: PlanningDay[] = [];

    for (
        let date = parseISO(resource.from);
        date <= parseISO(resource.to);
        date = addDays(date, 1)
    ) {
        const key = format(date, 'yyyy-MM-dd');

        days.push({
            date: key,
            label: format(date, 'EEEEEE d MMM', { locale: nl }).replace(/\.(?=\s|$)/g, ''),
            distanceKm: Math.round(perDay.get(key) ?? 0),
        });
    }

    return {
        from: resource.from,
        to: resource.to,
        weekNumber: getISOWeek(parseISO(resource.from)),
        totalDistanceKm: resource.total_distance_km,
        unscheduledCount: resource.unscheduled_count,
        days,
        rows: [...rows.values()].sort((a, b) => a.name.localeCompare(b.name, 'nl')),
        suggestions: resource.suggestions.map(toSuggestion),
    };
}

/**
 * De dienstkleuren onder het rooster: die van de diensten die het bedrijf levert, plus die van
 * elk blokje in beeld, in de vaste volgorde van de legenda.
 *
 * Een bedrijf dat alleen de bouwtechnische keuring doet, ziet dus geen kleur voor het
 * energielabel of NEN 2767. De blokjes tellen mee zodat er nooit een kleur zonder uitleg staat,
 * ook niet zolang de diensten nog laden.
 */
export function legendServices(
    rows: readonly PlanningRow[],
    offered: readonly InspectionType[],
): Service[] {
    const present = new Set<Service>(offered.map((type) => type.service));

    for (const row of rows) {
        for (const cell of Object.values(row.cells)) {
            for (const stop of cell.stops) {
                present.add(stop.service);
            }
        }
    }

    return SERVICES.filter((service) => present.has(service));
}

/** Alle keuringen van één inspecteur op één dag, op tijd gesorteerd. */
export function stopsFor(row: PlanningRow, date: string): PlanningStop[] {
    return row.cells[date]?.stops ?? [];
}

/** Maandag tot en met vrijdag van de week waarin `day` valt. */
export function workWeekOf(day: Date): { from: string; to: string } {
    const monday = startOfISOWeek(day);

    return {
        from: format(monday, 'yyyy-MM-dd'),
        to: format(addDays(monday, 4), 'yyyy-MM-dd'),
    };
}

export interface PlanningParams {
    from: string;
    to: string;
    /** Uuid van één inspecteur. */
    inspectorUuid?: string;
}

export interface AutoScheduleResult {
    dryRun: boolean;
    scheduled: Array<{
        orderUuid: string;
        orderNumber: string;
        inspectorUuid: string | null;
        scheduledAt: string;
        addedKm: number;
    }>;
    skipped: Array<{ orderUuid: string; orderNumber: string; reason: string }>;
}

function toAutoScheduleResult(resource: AutoScheduleResultResource): AutoScheduleResult {
    return {
        dryRun: resource.dry_run,
        scheduled: resource.scheduled.map((row) => ({
            orderUuid: row.order_uuid,
            orderNumber: row.order_number,
            inspectorUuid: row.inspector_uuid,
            scheduledAt: row.scheduled_at,
            addedKm: row.added_km,
        })),
        skipped: resource.skipped.map((row) => ({
            orderUuid: row.order_uuid,
            orderNumber: row.order_number,
            reason: row.reason,
        })),
    };
}

export const planningApi = {
    /** `GET /planning?from=&to=&user=` */
    async week(params: PlanningParams): Promise<PlanningWeek> {
        return toPlanningWeek(
            unwrap(
                planningWeekResourceSchema,
                await api.get('/planning', {
                    query: { from: params.from, to: params.to, user: params.inspectorUuid },
                }),
            ),
        );
    },

    /**
     * `POST /planning/apply-suggestion` - één voorstel doorvoeren. Is het voorstel
     * inmiddels van het bord verdwenen, dan is het **409**
     * `planning-suggestion-expired`; de schrijfactie doet nooit alsof hij lukte.
     */
    async applySuggestion(suggestionId: string): Promise<Order> {
        return toOrder(
            unwrap(
                orderResourceSchema,
                await api.post(
                    '/planning/apply-suggestion',
                    { suggestion_id: suggestionId },
                    { cacheKey: false },
                ),
            ),
        );
    },

    /**
     * `POST /planning/auto-schedule` - gaten vullen. Met `dryRun` verandert er
     * niets: de planner ziet eerst wat er zou gebeuren.
     */
    async autoSchedule(input: {
        from: string;
        to: string;
        dryRun: boolean;
    }): Promise<AutoScheduleResult> {
        return toAutoScheduleResult(
            unwrap(
                autoScheduleResultResourceSchema,
                await api.post(
                    '/planning/auto-schedule',
                    { from: input.from, to: input.to, dry_run: input.dryRun },
                    { cacheKey: false },
                ),
            ),
        );
    },
};
